interface SectionBackdropProps {
  /** Visual personality of this section's backdrop */
  variant?: 'blue-cyan' | 'cyan-blue' | 'blue-indigo' | 'cyan-indigo';
  /** Where the primary glow orb is anchored */
  glowPosition?: 'top-right' | 'top-left' | 'center' | 'bottom-left' | 'bottom-right';
  /** Grid style */
  pattern?: 'dots' | 'lines' | 'cross' | 'isometric';
}

// Each variant: [primaryColor, secondaryColor, accentColor]
const VARIANTS: Record<NonNullable<SectionBackdropProps['variant']>, [string, string, string]> = {
  'blue-cyan':    ['#2563eb', '#06b6d4', '#3b82f6'],
  'cyan-blue':    ['#06b6d4', '#2563eb', '#0ea5e9'],
  'blue-indigo':  ['#2563eb', '#6366f1', '#818cf8'],
  'cyan-indigo':  ['#06b6d4', '#6366f1', '#2563eb'],
};

const GLOW_POSITIONS: Record<NonNullable<SectionBackdropProps['glowPosition']>, React.CSSProperties> = {
  'top-right':    { top: '-160px',  right: '-160px' },
  'top-left':     { top: '-160px',  left:  '-160px' },
  'center':       { top: '50%',     left:  '50%', transform: 'translate(-50%, -50%)' },
  'bottom-left':  { bottom: '-160px', left: '-160px' },
  'bottom-right': { bottom: '-160px', right: '-160px' },
};

const SectionBackdrop: React.FC<SectionBackdropProps> = ({
  variant = 'blue-cyan',
  glowPosition = 'top-right',
  pattern = 'dots',
}) => {
  const [primary, secondary, accent] = VARIANTS[variant];
  const primaryPos = GLOW_POSITIONS[glowPosition];

  // Isometric tile geometry:
  //   Three line families: +90° (vertical), +30°, -30° — all with equal perpendicular spacing d.
  //   Smallest repeating tile: W = 4d/√3, H = 2d
  //   At d=30: W ≈ 69.28px, H = 60px
  const d = 30;
  const W = (4 * d) / Math.sqrt(3); // ≈ 69.28
  const H = 2 * d;                   // = 60
  // tan(30°) = 1/√3, so for a line at +30°: dy = dx / √3
  // Over width W: dy = W/√3 = H  (corner to corner)
  const dy = H; // vertical rise for a diagonal crossing the full tile width

  const gridStyle: React.CSSProperties =
    pattern === 'dots'
      ? {
          backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.35) 1.5px, transparent 1.5px)',
          backgroundSize: '28px 28px',
        }
      : pattern === 'lines'
      ? {
          backgroundImage:
            'linear-gradient(to right, rgba(148,163,184,0.15) 1px, transparent 1px), ' +
            'linear-gradient(to bottom, rgba(148,163,184,0.15) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }
      : {
          backgroundImage:
            'linear-gradient(to right, rgba(148,163,184,0.12) 1px, transparent 1px), ' +
            'linear-gradient(to bottom, rgba(148,163,184,0.12) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        };

  const S = 'rgba(148,163,184,0.22)';

  return (
    <>
      {/* Grid pattern — isometric uses an inline SVG so lines reach true container edges */}
      {pattern === 'isometric' ? (
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/*
              patternUnits="userSpaceOnUse" + overflow="visible" on each line means the
              lines are drawn in real px coordinates and the pattern engine tiles them
              seamlessly. Lines inside the pattern CAN extend outside the tile cell —
              the browser clips nothing; adjacent tiles stitch perfectly.
            */}
            <pattern
              id="iso-grid"
              x="0"
              y="0"
              width={W}
              height={H}
              patternUnits="userSpaceOnUse"
            >
              {/* +90° vertical lines */}
              <line x1="0"     y1={-H} x2="0"     y2={H * 2} stroke={S} strokeWidth="0.8" />
              <line x1={W / 2} y1={-H} x2={W / 2} y2={H * 2} stroke={S} strokeWidth="0.8" />

              {/* +30° diagonals — each drawn well beyond cell so no end-caps inside viewport */}
              <line x1={-W} y1={H + dy} x2={W * 2} y2={-dy}       stroke={S} strokeWidth="0.8" />
              <line x1={-W / 2} y1={H + dy} x2={W * 2.5} y2={-dy} stroke={S} strokeWidth="0.8" />

              {/* -30° diagonals */}
              <line x1={W * 2} y1={H + dy} x2={-W}       y2={-dy} stroke={S} strokeWidth="0.8" />
              <line x1={W * 2.5} y1={H + dy} x2={-W / 2} y2={-dy} stroke={S} strokeWidth="0.8" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#iso-grid)" />
        </svg>
      ) : (
        <div aria-hidden="true" className="absolute inset-0" style={gridStyle} />
      )}

      {/* Radial vignette — darkens edges so grid fades out naturally */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 50%, transparent 40%, rgba(15,25,40,0.7) 100%)',
        }}
      />

      {/* Primary large glow — anchored to glowPosition */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none animate-glow-drift-1"
        style={{
          ...primaryPos,
          width: '700px',
          height: '700px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${primary}55 0%, ${primary}22 40%, transparent 70%)`,
          filter: 'blur(60px)',
        }}
      />

      {/* Secondary mid orb — always bottom-opposite */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none animate-glow-drift-2"
        style={{
          bottom: '-80px',
          right: '10%',
          width: '500px',
          height: '500px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${secondary}44 0%, ${secondary}18 45%, transparent 70%)`,
          filter: 'blur(50px)',
        }}
      />

      {/* Accent tight orb — small, bright, gives the "light source" pop */}
      <div
        aria-hidden="true"
        className="absolute pointer-events-none animate-glow-drift-1"
        style={{
          top: '30%',
          left: '20%',
          width: '280px',
          height: '280px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${accent}66 0%, ${accent}22 50%, transparent 70%)`,
          filter: 'blur(40px)',
          animationDelay: '-6s',
        }}
      />
    </>
  );
};

export default SectionBackdrop;
